import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { OpenInNewRounded } from '@mui/icons-material';
import { Alert, Box, Button, Chip, IconButton, Typography } from '@mui/material';
import axios from 'axios';
import { AUTHORITIES } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { NoData } from 'app/shared/components/no-data/NoData';
import { PageFilters } from 'app/shared/components/page-filters/PageFilters';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import { PaginationComponent } from 'app/shared/components/pagination/Pagination';
import { TableComponent } from 'app/shared/components/table-component/TableComponent';
import { IHeaderItem } from 'app/shared/interfaces/headerItemsInterface';
import { IListData } from 'app/shared/interfaces/listDataInterface';
import { ICompany } from 'app/shared/model/company.model';
import { PeriodicType } from 'app/shared/model/enumerations/periodicType';
import { StatusFilter } from 'app/shared/model/enumerations/status-filter.model';
import {
  convertDateTimeFromServer,
  convertDateTimeToPtBRWithYear,
  convertDateTimeToPtBRWithYearWithoutSeconds,
} from 'app/shared/util/date-utils';
import { Loading } from 'app/shared/util/loading';
import { ASC, DEFAULT_ITEMS_PER_PAGE, DESC } from 'app/shared/util/pagination.constants';
import React, { useCallback, useEffect, useState } from 'react';
import { translate } from 'react-jhipster';
import { Link, RouteComponentProps } from 'react-router-dom';
import { ICameraEvent } from 'app/shared/model/camera-event.model';
import { getEntities, reset } from './camera-event.reducer';

const MAXIMUM_PERIOD_MILLISECONDS = 31 * 24 * 60 * 60 * 1000;

interface IAlarmType {
  code: string;
  name: string;
  category: string;
}

interface IFilterOption {
  id: string;
  name: string;
  category?: string;
}

interface IVehicleOption {
  id: number;
  licensePlate?: string;
}

interface IAppliedFilters {
  truckImplementId?: number | null;
  dateStart: string | null;
  dateEnd: string | null;
  periodicType: PeriodicType | null;
  eventType: IFilterOption | null;
  companyId: number | null;
  searchCompanyName: string;
  searchLicensePlate: string;
}

const startOfDay = (date: Date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const endOfDay = (date: Date) => {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
};

const periodRange = (period: PeriodicType) => {
  const now = new Date();
  if (period === PeriodicType.TODAY) {
    return { start: convertDateTimeFromServer(startOfDay(now)), end: convertDateTimeFromServer(now) };
  }
  if (period === PeriodicType.YESTERDAY) {
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    return { start: convertDateTimeFromServer(startOfDay(yesterday)), end: convertDateTimeFromServer(endOfDay(yesterday)) };
  }
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - 7);
  return { start: convertDateTimeFromServer(weekStart), end: convertDateTimeFromServer(now) };
};

const isCustomPeriod = (period: PeriodicType | null) => period === PeriodicType.CUSTOM;

const effectivePeriodRange = (period: PeriodicType, dateStart: string | null, dateEnd: string | null) => {
  if (isCustomPeriod(period)) {
    return { start: dateStart, end: dateEnd };
  }
  return periodRange(period);
};

const getAppliedPeriodLabel = (period: PeriodicType | null, dateStart: string | null, dateEnd: string | null) => {
  if (period !== PeriodicType.CUSTOM || !dateStart || !dateEnd) return '';
  return `${convertDateTimeToPtBRWithYearWithoutSeconds(dateStart)} - ${convertDateTimeToPtBRWithYearWithoutSeconds(dateEnd)}`;
};

const normalizeDateInput = (value: any): string | null => {
  if (!value) return null;
  if (typeof value.toISOString === 'function') return value.toISOString();

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

const formatSpeed = (value?: number | null) => (value == null ? '-' : `${value} km/h`);

const mediaStatusLabel = (event: ICameraEvent) => {
  const expected = event.mediaCount ?? event.attachCount ?? 0;
  if (!expected) return 'Sem mídia';
  return expected === 1 ? '1 mídia' : `${expected} mídias`;
};

const isValidCoordinate = (latitude?: number | null, longitude?: number | null) => latitude != null && longitude != null && latitude !== 0 && longitude !== 0;

const mediaColor = (event: ICameraEvent) => {
  const expected = event.mediaCount ?? event.attachCount ?? 0;
  if (!expected) return 'default';
  return 'info';
};

const alarmChipColor = (alarmClass?: string | null) => {
  switch (alarmClass) {
    case 'badge-adas':
      return 'info';
    case 'badge-dsm':
      return 'warning';
    case 'badge-harsh':
      return 'error';
    case 'badge-system':
      return 'default';
    default:
      return 'default';
  }
};

interface CameraEventProps extends RouteComponentProps {
  embedded?: boolean;
  excludeSystemEvents?: boolean;
  fixedTruckImplementId?: number;
  alertTypeFilter?: Pick<React.ComponentProps<typeof PageFilters>, 'activeButton' | 'handleActiveButton' | 'ActiveButton' | 'alertTypeSelect'>;
}

type PageFilterName = NonNullable<React.ComponentProps<typeof PageFilters>['showFilters']>[number];

const cameraFilterLayout = (embedded: boolean, fixedTruck: boolean): Partial<React.ComponentProps<typeof PageFilters>> => {
  const vehicleFilters: PageFilterName[] = fixedTruck ? [] : ['company', 'licensePlateSelect'];
  const showFilters: PageFilterName[] = embedded
    ? ['periodic', ...vehicleFilters, 'alert', 'eventSelect', 'searchButton', 'clearFilters']
    : ['company', 'periodic', 'licensePlateSelect', 'eventSelect', 'searchButton', 'clearFilters'];
  return {
    showFilters,
    ...(embedded && {
      periodSelect: !fixedTruck,
      filterWidthSm: '180px',
      filterWidthMd: '200px',
    }),
  };
};

const initialPeriodRange = (params: URLSearchParams, period: PeriodicType, embedded: boolean) => {
  const range = periodRange(period);
  if (!embedded && !isCustomPeriod(period)) return range;

  return {
    start: params.get('startAt') || range.start,
    end: params.get('endAt') || range.end,
  };
};

const isMissingRequiredCompany = (isAdmin: boolean, companyId?: number | null, fixedTruckImplementId?: number) =>
  isAdmin && !companyId && !fixedTruckImplementId;

const hasRequiredEventFilters = (
  periodicType: PeriodicType | null,
  isAdmin: boolean,
  companyId?: number | null,
  fixedTruckImplementId?: number
) => Boolean(periodicType) && (!isAdmin || Boolean(companyId) || !!fixedTruckImplementId);

const getEmptyStateMessage = (embedded: boolean, hasSearched: boolean, hasRequiredFilters: boolean, isAdmin: boolean, periodicType: PeriodicType | null) =>
  embedded && !hasSearched
    ? 'Ajuste os filtros e clique em buscar para consultar os alertas.'
    : embedded
    ? 'Nenhum dado encontrado'
    : hasRequiredFilters
    ? translate('fleetSenseApp.cameraEvent.home.notFound')
    : translate(
        isAdmin && periodicType
          ? 'fleetSenseApp.cameraEvent.home.selectAdminCompany'
          : isAdmin
          ? 'fleetSenseApp.cameraEvent.home.selectAdminFilters'
          : 'fleetSenseApp.cameraEvent.home.selectCompanyFilters'
      );

export const CameraEvent = (props: CameraEventProps) => {
  const { embedded = false, excludeSystemEvents = false, fixedTruckImplementId } = props;
  const dispatch = useAppDispatch();
  const urlParams = new URLSearchParams(props.location.search);
  const eventList = useAppSelector(state => state.cameraEvent.entities);
  const loading = useAppSelector(state => state.cameraEvent.loading);
  const requestError = useAppSelector(state => state.cameraEvent.errorMessage);
  const totalItems = useAppSelector(state => state.cameraEvent.totalItems || 0);
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));

  const urlCompanyId = urlParams.get('companyId');
  const urlCompanyName = urlParams.get('companyName') || '';
  const urlLicensePlate = urlParams.get('licensePlate') || '';
  const urlEventType = embedded ? null : urlParams.get('eventType');
  const urlEventTypeName = urlParams.get('eventTypeName');
  const initialPeriodicType = (urlParams.get('periodicType') as PeriodicType | null) || PeriodicType.TODAY;
  const { start: initialDateStart, end: initialDateEnd } = initialPeriodRange(urlParams, initialPeriodicType, embedded);
  const [activePage, setActivePage] = useState(Number(urlParams.get('page')) || 1);
  const [sortBy, setSortBy] = useState(urlParams.get('sortBy') || 'occurredAt');
  const [sortOrder, setSortOrder] = useState(urlParams.get('order') || DESC);
  const [periodicType, setPeriodicType] = useState<PeriodicType | null>(initialPeriodicType);
  const [dateStart, setDateStart] = useState<string | null>(initialDateStart);
  const [dateEnd, setDateEnd] = useState<string | null>(initialDateEnd);
  const [customPeriodApplied, setCustomPeriodApplied] = useState(
    initialPeriodicType === PeriodicType.CUSTOM && Boolean(initialDateStart && initialDateEnd)
  );
  const [isPeriodicCustom, setIsPeriodicCustom] = useState(false);
  const [tempDateStart, setTempDateStart] = useState<string | null>(null);
  const [tempDateEnd, setTempDateEnd] = useState<string | null>(null);
  const [eventType, setEventType] = useState<IFilterOption | null>(urlEventType ? { id: urlEventType, name: urlEventTypeName || urlEventType } : null);
  const [licensePlateOption, setLicensePlateOption] = useState<IVehicleOption | null>(
    urlLicensePlate ? { id: Number(urlParams.get('truckImplementId')), licensePlate: urlLicensePlate } : null
  );
  const [searchLicensePlate, setSearchLicensePlate] = useState(urlLicensePlate);
  const [selectedCompany, setSelectedCompany] = useState<ICompany | null>(
    urlCompanyName ? ({ id: urlCompanyId ? Number(urlCompanyId) : 0, name: urlCompanyName } as ICompany) : null
  );
  const [searchCompanyName, setSearchCompanyName] = useState(urlCompanyName);
  const [filterError, setFilterError] = useState<string | null>(null);
  const [alarmTypes, setAlarmTypes] = useState<IAlarmType[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<IAppliedFilters>({
    truckImplementId: Number(urlParams.get('truckImplementId')),
    dateStart: initialDateStart,
    dateEnd: initialDateEnd,
    periodicType: initialPeriodicType,
    eventType: urlEventType ? { id: urlEventType, name: urlEventTypeName || urlEventType } : null,
    companyId: urlCompanyId ? Number(urlCompanyId) : null,
    searchCompanyName: urlCompanyName,
    searchLicensePlate: urlLicensePlate,
  });
  const [hasSearched, setHasSearched] = useState(!embedded);
  const paginationState = { activePage, itemsPerPage: DEFAULT_ITEMS_PER_PAGE, sort: sortBy, order: sortOrder };

  useEffect(() => {
    axios
      .get<IAlarmType[]>('api/camera-events/alarm-types')
      .then(response => setAlarmTypes(response.data))
      .catch(() => setAlarmTypes([]));
  }, []);

  const getAllEntities = useCallback(() => {
    if (embedded && !hasSearched) return;
    if (!appliedFilters.periodicType || isMissingRequiredCompany(isAdmin, appliedFilters.companyId, fixedTruckImplementId)) {
      setFilterError(null);
      dispatch(reset());
      return;
    }

    const effectiveRange = embedded
      ? { start: appliedFilters.dateStart, end: appliedFilters.dateEnd }
      : effectivePeriodRange(appliedFilters.periodicType, appliedFilters.dateStart, appliedFilters.dateEnd);
    const startDate = effectiveRange.start ? new Date(effectiveRange.start) : null;
    const endDate = effectiveRange.end ? new Date(effectiveRange.end) : null;
    if (
      !startDate ||
      !endDate ||
      Number.isNaN(startDate.getTime()) ||
      Number.isNaN(endDate.getTime()) ||
      startDate > endDate ||
      endDate.getTime() - startDate.getTime() > MAXIMUM_PERIOD_MILLISECONDS
    ) {
      setFilterError(translate('fleetSenseApp.cameraEvent.filters.invalidPeriod'));
      return;
    }

    setFilterError(null);
    dispatch(
      getEntities({
        excludeSystemEvents,
        searchCompanyName: isAdmin ? appliedFilters.searchCompanyName || null : null,
        startAt: startDate.toISOString(),
        endAt: endDate.toISOString(),
        eventType: appliedFilters.eventType?.id || null,
        licensePlateSearch: appliedFilters.searchLicensePlate.trim() || null,
        truckImplementId: fixedTruckImplementId || null,
        page: activePage - 1,
        size: DEFAULT_ITEMS_PER_PAGE,
        sort: `${sortBy},${sortOrder}`,
      })
    );
  }, [activePage, appliedFilters, dispatch, isAdmin, sortBy, sortOrder, excludeSystemEvents, embedded, hasSearched, fixedTruckImplementId]);

  useEffect(() => {
    getAllEntities();
  }, [getAllEntities]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (embedded) params.set('tab', 'cameras');
    if (embedded && appliedFilters.truckImplementId) params.set('truckImplementId', String(appliedFilters.truckImplementId));
    params.set('page', String(activePage));
    params.set('sortBy', sortBy);
    params.set('order', sortOrder);
    if ((embedded || isCustomPeriod(appliedFilters.periodicType)) && appliedFilters.dateStart) {
      params.set('startAt', appliedFilters.dateStart);
    }
    if ((embedded || isCustomPeriod(appliedFilters.periodicType)) && appliedFilters.dateEnd) {
      params.set('endAt', appliedFilters.dateEnd);
    }
    if (appliedFilters.periodicType) {
      params.set('periodicType', appliedFilters.periodicType);
    }
    if (appliedFilters.eventType?.id) {
      params.set('eventType', appliedFilters.eventType.id);
      params.set('eventTypeName', appliedFilters.eventType.name);
    }
    if (isAdmin && appliedFilters.searchCompanyName) {
      params.set('companyName', appliedFilters.searchCompanyName);
      if (appliedFilters.companyId) {
        params.set('companyId', String(appliedFilters.companyId));
      }
    }
    if (appliedFilters.searchLicensePlate) {
      params.set('licensePlate', appliedFilters.searchLicensePlate);
    }
    const search = `?${params.toString()}`;
    if (props.location.search !== search) {
      props.history.replace(`${props.location.pathname}${search}`);
    }
  }, [activePage, appliedFilters, isAdmin, props.history, props.location.pathname, props.location.search, sortBy, sortOrder, embedded]);

  const handleSearchCompany = (company: ICompany) => {
    setSelectedCompany(company || null);
    setSearchCompanyName(company?.name || '');
    setLicensePlateOption(null);
    setSearchLicensePlate('');
  };

  const handleSearchLicensePlate = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchLicensePlate(event.target.value);
  };

  const handleSearchLicensePlateSelect = (value: IVehicleOption | null) => {
    setLicensePlateOption(value);
    setSearchLicensePlate(value?.licensePlate || '');
  };

  const resetActivePage = (page = 1) => setActivePage(page);

  const sort = p => () => {
    setSortOrder(sortBy === p && sortOrder === ASC ? DESC : ASC);
    setSortBy(p);
    setActivePage(1);
  };

  const sendSensorDataByImplementIdAndPeriodic = (period: PeriodicType) => {
    const range = periodRange(period);
    setPeriodicType(period);
    setDateStart(range.start);
    setDateEnd(range.end);
    setCustomPeriodApplied(false);
  };

  const changeToCustomPeriodic = () => {
    setIsPeriodicCustom(true);
  };

  const sendSensorDataByImplement = event => {
    event?.preventDefault?.();
    setPeriodicType(PeriodicType.CUSTOM);
    setDateStart(tempDateStart);
    setDateEnd(tempDateEnd);
    setCustomPeriodApplied(Boolean(tempDateStart && tempDateEnd));
    setIsPeriodicCustom(false);
  };

  const handleTempDateStartChange = (value: any) => {
    setTempDateStart(normalizeDateInput(value));
  };

  const handleTempDateEndChange = (value: any) => {
    setTempDateEnd(normalizeDateInput(value));
  };

  const sortClear = () => {
    const todayRange = periodRange(PeriodicType.TODAY);
    setPeriodicType(PeriodicType.TODAY);
    setCustomPeriodApplied(false);
    setDateStart(todayRange.start);
    setDateEnd(todayRange.end);
    setTempDateStart(todayRange.start);
    setTempDateEnd(todayRange.end);
    setEventType(null);
    if (!fixedTruckImplementId) {
      setLicensePlateOption(null);
      setSelectedCompany(null);
      setSearchLicensePlate('');
      setSearchCompanyName('');
    }
    setAppliedFilters({
      dateStart: null,
      dateEnd: null,
      periodicType: null,
      eventType: null,
      companyId: fixedTruckImplementId ? selectedCompany?.id || null : null,
      searchCompanyName: fixedTruckImplementId ? searchCompanyName : '',
      searchLicensePlate: fixedTruckImplementId ? searchLicensePlate : '',
      truckImplementId: fixedTruckImplementId || undefined,
    });
    if (embedded) {
      setHasSearched(false);
      dispatch(reset());
    }
    if (activePage !== 1) {
      setActivePage(1);
    }
  };

  const openDetail = (event: ICameraEvent) => {
    const returnTo = `${props.location.pathname}${props.location.search}`;
    props.history.push(`/camera-event/${encodeURIComponent(event.hatDeviceCode)}/${encodeURIComponent(event.hatEventId)}`, { event, returnTo });
  };

  const hasRequiredFilters = hasRequiredEventFilters(appliedFilters.periodicType, isAdmin, appliedFilters.companyId, fixedTruckImplementId);
  const emptyStateMessage = getEmptyStateMessage(embedded, hasSearched, hasRequiredFilters, isAdmin, appliedFilters.periodicType);
  const appliedPeriodLabel = embedded && customPeriodApplied ? getAppliedPeriodLabel(periodicType, dateStart, dateEnd) : '';
  const hasCameraFilters = periodicType !== null || Boolean(eventType || searchCompanyName || searchLicensePlate);
  const companySelectApiUrl = 'api/admin/camera-devices/companies/options';
  const licensePlateSelectApiUrl =
    isAdmin && selectedCompany?.id ? `api/admin/camera-devices/options?searchCompanyId=${selectedCompany.id}` : 'api/admin/camera-devices/options';

  const applyFilters = () => {
    const range = embedded && periodicType ? effectivePeriodRange(periodicType, dateStart, dateEnd) : { start: dateStart, end: dateEnd };
    setAppliedFilters({
      dateStart: range.start,
      dateEnd: range.end,
      periodicType,
      eventType,
      truckImplementId: fixedTruckImplementId || licensePlateOption?.id,
      companyId: selectedCompany?.id || null,
      searchCompanyName,
      searchLicensePlate,
    });
    setHasSearched(true);
    if (activePage !== 1) {
      setActivePage(1);
    }
  };

  const alarmTypeOptions: IFilterOption[] = alarmTypes
    .filter(type => (isAdmin && !excludeSystemEvents) || type.category !== 'badge-system')
    .map(type => ({
      id: type.code,
      name: type.name,
      category: type.category,
    }));

  const headers: IHeaderItem[] = [
    { key: 'occurredAt', sortable: true, component: <>{translate('fleetSenseApp.cameraEvent.occurredAt')}</> },
    { key: 'alarmName', sortable: true, component: <>{translate('fleetSenseApp.cameraEvent.alarmName')}</> },
    { key: 'truckImplementLicensePlate', sortable: true, component: <>{translate('fleetSenseApp.cameraEvent.truckImplement')}</> },
    { key: 'speed', sortable: true, component: <>{translate('fleetSenseApp.cameraEvent.speed')}</> },
    { key: 'location', sortable: true, component: <>{translate('fleetSenseApp.cameraEvent.location')}</> },
    { key: 'mediaCount', sortable: true, component: <>{translate('fleetSenseApp.cameraEvent.mediaCount')}</> },
  ];

  const data: IListData[] = [
    {
      type: 'child',
      value: 'occurredAt',
      renderTableChild: event => <Typography color="primary.contrastText">{convertDateTimeToPtBRWithYear(event.occurredAt)}</Typography>,
    },
    {
      type: 'child',
      value: 'alarmName',
      renderTableChild: event => <Chip color={alarmChipColor(event.alarmClass) as any} label={event.alarmName || '-'} size="small" variant="outlined" />,
    },
    {
      type: 'child',
      value: 'truckImplementLicensePlate',
      renderTableChild: (event: ICameraEvent) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography color="primary.contrastText" fontSize="0.875rem">
            {event.truckImplementLicensePlate || '-'}
          </Typography>
          <IconButton
            color="secondary"
            component={Link}
            to={`/dashboards/truck-implement/${event.truckImplementId}`}
            size="small"
            disabled={!event.truckImplementId}
            aria-label="Visualizar ativo"
          >
            <OpenInNewRounded fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    {
      type: 'child',
      value: 'speed',
      renderTableChild: event => <Typography color="primary.contrastText">{formatSpeed(event.speed)}</Typography>,
    },
    {
      type: 'child',
      value: 'location',
      renderTableChild: event => (
        <Chip
          color={isValidCoordinate(event.latitude, event.longitude) ? 'success' : 'default'}
          label={translate(isValidCoordinate(event.latitude, event.longitude) ? 'fleetSenseApp.cameraEvent.withGps' : 'fleetSenseApp.cameraEvent.withoutGps')}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      type: 'child',
      value: 'mediaCount',
      renderTableChild: event => <Chip color={mediaColor(event) as any} label={mediaStatusLabel(event)} size="small" variant="outlined" />,
    },
    {
      type: 'child',
      value: 'details',
      renderTableChild: event => (
        <Button
          color="secondary"
          onClick={() => openDetail(event)}
          size="small"
          startIcon={<ManageSearchIcon fontSize="small" />}
          variant="outlined"
          sx={{ minWidth: 104, textTransform: 'none', fontWeight: 600 }}
        >
          {translate('fleetSenseApp.cameraEvent.actions.details')}
        </Button>
      ),
    },
  ];

  return (
    <Box sx={embedded ? { minWidth: 0 } : { backgroundColor: 'primary.main', borderRadius: '8px', padding: '24px' }}>
      {!embedded && (
        <PageHeader headingId="camera-event-heading" headingDataCy="CameraEventHeading" contentKey="fleetSenseApp.cameraEvent.home.title" showButton={false} />
      )}

      <PageFilters
        setActivePage={resetActivePage}
        isAdmin={isAdmin}
        getAllEntities={applyFilters}
        handleSearchCompany={handleSearchCompany}
        companySelectApiUrl={companySelectApiUrl}
        companySelectClearable
        searchCompanyName={searchCompanyName}
        searchLicensePlate={searchLicensePlate}
        handleSearchLicensePlate={handleSearchLicensePlate}
        licensePlateSelectValue={licensePlateOption}
        handleSearchLicensePlateSelect={handleSearchLicensePlateSelect}
        licensePlateSelectApiUrl={licensePlateSelectApiUrl}
        licensePlateSelectDisabled={isAdmin && !selectedCompany?.id}
        licensePlateSelectClearable
        sortClear={sortClear}
        loading={loading}
        status={hasCameraFilters ? StatusFilter.ALL : StatusFilter.ACTIVATED}
        periodicType={periodicType || undefined}
        sendSensorDataByImplementIdAndPeriodic={sendSensorDataByImplementIdAndPeriodic}
        changeToCustomPeriodic={changeToCustomPeriodic}
        sendSensorDataByImplement={sendSensorDataByImplement}
        setDateStart={handleTempDateStartChange}
        dateStart={tempDateStart}
        setDateEnd={handleTempDateEndChange}
        dateEnd={tempDateEnd}
        isOpen={isPeriodicCustom}
        toggle={() => setIsPeriodicCustom(!isPeriodicCustom)}
        isCompany={false}
        maxRangeDays={31}
        submitLabel={translate('fleetSenseApp.cameraEvent.filters.applyPeriod')}
        appliedPeriodLabel={appliedPeriodLabel || undefined}
        compactClearFilters
        searchButtonDisabled={isAdmin && !selectedCompany?.id && !fixedTruckImplementId}
        filterWidthSm="180px"
        filterWidthMd="220px"
        eventSelectValue={eventType}
        eventSelectOptions={alarmTypeOptions}
        handleEventSelect={setEventType}
        eventSelectLabel={translate('fleetSenseApp.cameraEvent.filters.event')}
        eventSelectPlaceholder={translate('fleetSenseApp.cameraEvent.filters.allEvents')}
        {...props.alertTypeFilter}
        {...cameraFilterLayout(embedded, !!fixedTruckImplementId)}
      />

      {filterError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {filterError}
        </Alert>
      )}
      {!loading && requestError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Não foi possível carregar os alertas de câmera. Tente buscar novamente.
        </Alert>
      )}
      {!loading && !requestError && !filterError && !!eventList.length && (
        <TableComponent
          listData={data}
          list={eventList}
          headers={headers}
          match={props.match}
          sortFunction={sort}
          pagination={paginationState}
          hideTrailingHeaderCell
        />
      )}
      {!loading && !requestError && !eventList.length && !filterError && (
        <NoData title={emptyStateMessage} height="clamp(360px, calc(100vh - 360px), 560px)" showImage={hasRequiredFilters && (!embedded || hasSearched)} />
      )}
      {loading && <Loading />}
      {!loading && !requestError && !filterError && (
        <PaginationComponent paginationState={paginationState} totalItems={totalItems} list={eventList} onChange={(_event, page) => setActivePage(page)} />
      )}
    </Box>
  );
};

export default CameraEvent;
